import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2, Car } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * Dialog réutilisable de création rapide d'un véhicule pour un client donné.
 * Utilisé depuis le Calendrier (optionnel) et la création d'OR (obligatoire).
 *
 * Champs OBLIGATOIRES : marque, modele (Type), plaque, vin (Châssis), km_dernier, premiere_mec.
 *
 * onCreated reçoit l'objet vehicule complet renvoyé par l'API.
 */
export default function QuickVehiculeDialog({ open, onClose, onCreated, clientId, clientNom }) {
  const [form, setForm] = useState({
    marque: "", modele: "", plaque: "", vin: "", km_dernier: "", premiere_mec: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({ marque: "", modele: "", plaque: "", vin: "", km_dernier: "", premiere_mec: "" });
    }
  }, [open]);

  const submit = async () => {
    // Validation côté client : tous champs obligatoires
    const errs = [];
    if (!form.marque.trim()) errs.push("Marque");
    if (!form.modele.trim()) errs.push("Type / modèle");
    if (!form.plaque.trim()) errs.push("N° de plaques");
    if (!form.vin.trim()) errs.push("N° de châssis (VIN)");
    if (form.km_dernier === "" || isNaN(parseInt(form.km_dernier, 10))) errs.push("Kilométrage");
    if (!form.premiere_mec) errs.push("1ère mise en circulation");
    if (errs.length) {
      toast.error("Champs requis manquants : " + errs.join(", "));
      return;
    }
    // VIN suisse : exactement 17 caractères alphanumériques (sans I/O/Q)
    if (form.vin.length !== 17) {
      toast.error("Le n° de châssis (VIN) doit faire exactement 17 caractères.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        plaque: form.plaque.trim().toUpperCase(),
        vin: form.vin.trim().toUpperCase(),
        marque: form.marque.trim(),
        modele: form.modele.trim(),
        km_dernier: parseInt(form.km_dernier, 10),
        premiere_mec: form.premiere_mec,
        annee: form.premiere_mec ? parseInt(form.premiere_mec.slice(0, 4), 10) : null,
        est_principal: true,
      };
      // iter66 — Pré-check unicité plaque
      try {
        const { data: chk } = await api.get("/vehicules/check-plaque", { params: { plaque: payload.plaque } });
        if (!chk.available && chk.conflict) {
          const c = chk.conflict;
          toast.error(`Plaque déjà attribuée à ${c.marque || ""} ${c.modele || ""} de ${c.client_nom}. Choisissez une autre plaque.`);
          setSubmitting(false);
          return;
        }
      } catch (e) { /* silent — le backend revérifiera */ }
      const { data } = await api.post(`/clients/${clientId}/vehicules`, payload);
      toast.success(`Véhicule ${payload.marque} ${payload.modele} créé`);
      onCreated(data);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="quick-vehicule-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Car className="w-5 h-5 text-[#52a675]" /> Ajouter un véhicule
          </DialogTitle>
          <DialogDescription>
            {clientNom && <>Pour <span className="font-semibold">{clientNom}</span>. </>}
            Tous les champs sont obligatoires. Les détails (assurance, services, etc.) pourront être complétés depuis la fiche véhicule.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Marque *</Label>
              <Input value={form.marque} onChange={(e) => setForm({ ...form, marque: e.target.value })} placeholder="VW, Audi, Tesla..." data-testid="qv-marque" />
            </div>
            <div>
              <Label>Type / Modèle *</Label>
              <Input value={form.modele} onChange={(e) => setForm({ ...form, modele: e.target.value })} placeholder="Golf 7 GTI, Q5, Model 3..." data-testid="qv-modele" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>N° de plaques *</Label>
              <Input
                value={form.plaque}
                onChange={(e) => setForm({ ...form, plaque: e.target.value.toUpperCase() })}
                placeholder="GE 123 456"
                data-testid="qv-plaque"
              />
            </div>
            <div>
              <Label>N° de châssis (VIN) *</Label>
              <Input
                value={form.vin}
                onChange={(e) => setForm({ ...form, vin: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "") })}
                maxLength={17}
                placeholder="17 caractères"
                className="font-mono"
                data-testid="qv-vin"
              />
              <div className="text-[10px] text-gray-400 mt-0.5">{form.vin.length}/17</div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Kilométrage *</Label>
              <Input
                type="number"
                value={form.km_dernier}
                onChange={(e) => setForm({ ...form, km_dernier: e.target.value })}
                placeholder="Ex: 87'400"
                data-testid="qv-km"
              />
            </div>
            <div>
              <Label>1ère mise en circulation *</Label>
              <Input
                type="date"
                value={form.premiere_mec}
                onChange={(e) => setForm({ ...form, premiere_mec: e.target.value })}
                data-testid="qv-premiere-mec"
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={submit} disabled={submitting} className="bg-[#52a675] hover:bg-[#3d8a5c]" data-testid="quick-vehicule-save">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Créer le véhicule"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
